import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { X, Upload, File, Folder, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import fileService from '../services/fileService';
import { useUpload } from '../context/UploadContext';

const UploadModal = ({
  isOpen,
  onClose,
  dataroomUuid,
  currentFolder,
  onUploadComplete
}) => {
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [emptyFolders, setEmptyFolders] = useState([]); // Track empty folders
  const { addUploadJob, startPolling } = useUpload();

  // Process files from folder upload
  const processFileList = (fileList) => {
    const files = Array.from(fileList);
    return files.map((file) => ({
      file,
      name: file.name,
      path: file.webkitRelativePath || file.name,
      size: file.size,
      status: 'pending', // pending, uploading, completed, error
      progress: 0,
      error: null
    }));
  };

  // Handle dropped files
  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) => ({
      file,
      name: file.name,
      path: file.webkitRelativePath || file.name,
      size: file.size,
      status: 'pending',
      progress: 0,
      error: null
    }));

    setUploadQueue(prev => [...prev, ...newFiles]);
  }, []);

  // Handle folder selection
  const handleFolderSelect = (e) => {
    const files = processFileList(e.target.files);
    const fileList = Array.from(e.target.files);

    console.log('Folder selected - files count:', fileList.length);

    if (fileList.length === 0) {
      // Completely empty folder selected - browser security limitation
      // Browsers cannot read empty folders at all - this is a security feature
      toast.error('Cannot upload completely empty folders due to browser security restrictions. Please add at least one file to the folder.');

      // Reset the input
      e.target.value = '';
      return;
    }

    setUploadQueue(prev => [...prev, ...files]);

    // Extract all folder paths from files
    const allFolders = new Set();
    fileList.forEach(file => {
      if (file.webkitRelativePath) {
        const parts = file.webkitRelativePath.split('/');
        // Build all folder paths
        let currentPath = '';
        for (let i = 0; i < parts.length - 1; i++) {
          currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
          allFolders.add(currentPath);
        }
      }
    });

    // Detect empty folders by checking which folders don't have files
    const emptyFoldersList = [];
    allFolders.forEach(folderPath => {
      // Check if this folder has any direct files
      const hasFiles = fileList.some(file => {
        if (!file.webkitRelativePath) return false;
        const fileFolderPath = file.webkitRelativePath.split('/').slice(0, -1).join('/');
        return fileFolderPath === folderPath;
      });

      if (!hasFiles) {
        emptyFoldersList.push(folderPath);
      }
    });

    if (emptyFoldersList.length > 0) {
      setEmptyFolders(prev => [...prev, ...emptyFoldersList]);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = processFileList(e.target.files);
    setUploadQueue(prev => [...prev, ...files]);
  };

  // Remove file from queue
  const removeFile = (index) => {
    setUploadQueue(prev => prev.filter((_, i) => i !== index));
  };

  // Clear all files
  const clearAll = () => {
    setUploadQueue([]);
    setEmptyFolders([]);
  };

  // Extract folder structure from file paths
  const extractFolderStructure = () => {
    const folderSet = new Set();

    // Add folders from files
    uploadQueue.forEach(item => {
      if (item.path && item.path.includes('/')) {
        // Get the directory path (everything except the file name)
        const pathParts = item.path.split('/');
        pathParts.pop(); // Remove file name

        // Build nested folder paths including the root folder
        let currentPath = '';
        pathParts.forEach(part => {
          currentPath = currentPath ? `${currentPath}/${part}` : part;
          folderSet.add(currentPath);
        });
      }
    });

    // Add empty folders
    emptyFolders.forEach(folder => {
      folderSet.add(folder);
    });

    // Sort folders by depth (parent folders first)
    return Array.from(folderSet).sort((a, b) => {
      return a.split('/').length - b.split('/').length;
    });
  };

  // Upload all files with folder hierarchy
  const handleUploadAll = async () => {
    console.log('Upload clicked - uploadQueue:', uploadQueue.length, 'emptyFolders:', emptyFolders.length);
    console.log('Empty folders array:', emptyFolders);

    // Check if we have files or folders to upload
    if (uploadQueue.length === 0 && emptyFolders.length === 0) {
      toast.error('No files or folders to upload');
      return;
    }

    setIsUploading(true);

    try {
      // Prepare files data for bulk upload
      const filesData = uploadQueue.map(item => ({
        file: item.file,
        path: item.path // Include the full path with folder structure
      }));

      // Get all folders (including empty ones)
      const allFolders = extractFolderStructure();

      // Call bulk upload API
      console.log('📤 Starting bulk upload...');
      console.log('📁 Current folder UUID:', currentFolder);
      console.log('📄 Files to upload:', filesData.length);
      console.log('📂 Folders to create:', allFolders);

      const response = await fileService.bulkUploadWithFolders(
        dataroomUuid,
        filesData,
        currentFolder, // Parent folder UUID
        allFolders // Send all folders including empty ones
      );

      console.log('✅ Upload response:', response);

      // Check if we got an import job UUID for background tracking
      if (response.data?.import_job_uuid || response.import_job_uuid) {
        const jobUuid = response.data?.import_job_uuid || response.import_job_uuid;

        console.log('📋 Import job UUID:', jobUuid);

        // Get file/folder counts
        const folderCount = extractFolderStructure().length;
        const fileCount = uploadQueue.length;

        // Create upload name
        let uploadName = 'Bulk Upload';
        if (fileCount > 0 && folderCount > 0) {
          uploadName = `${fileCount} file(s) & ${folderCount} folder(s)`;
        } else if (folderCount > 0) {
          uploadName = `${folderCount} folder(s)`;
        } else if (fileCount > 0) {
          uploadName = `${fileCount} file(s)`;
        }

        // Add job to upload tracker
        addUploadJob({
          importJobUuid: jobUuid,
          dataroomUuid,
          fileName: uploadName,
          fileCount,
          folderCount
        });

        // Start polling in background
        startPolling(dataroomUuid, jobUuid);

        // Show background upload notification
        toast.success('Files uploaded! Processing in background...', {
          duration: 3000,
          icon: '📤'
        });

        // Close modal and clear queue
        setUploadQueue([]);
        setEmptyFolders([]);
        setIsUploading(false);
        onClose();

        // Refresh file list
        if (onUploadComplete) {
          console.log('🔄 Calling onUploadComplete to refresh file list...');
          onUploadComplete();
        }
      } else {
        // Fallback for older API response without import job UUID
        console.log('⚠️ No import job UUID received, using immediate completion');

        // Show success message
        const folderCount = extractFolderStructure().length;
        const fileCount = uploadQueue.length;

        if (fileCount > 0 && folderCount > 0) {
          toast.success(
            `Successfully uploaded ${fileCount} file(s) and created ${folderCount} folder(s)`
          );
        } else if (folderCount > 0) {
          toast.success(`Successfully created ${folderCount} folder(s)`);
        } else {
          toast.success(`Successfully uploaded ${fileCount} file(s)`);
        }

        // Clear and close
        setUploadQueue([]);
        setEmptyFolders([]);
        setIsUploading(false);
        onClose();

        // Refresh file list
        if (onUploadComplete) {
          console.log('🔄 Calling onUploadComplete to refresh file list...');
          onUploadComplete();
        }
      }

    } catch (error) {
      console.error('Upload error:', error);

      toast.error(
        error.response?.data?.message ||
        'Failed to upload files. Please try again.'
      );

      setIsUploading(false);
    }
  };

  // Format file size
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={!isUploading ? onClose : undefined}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-4xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Upload Files & Folders
            </h3>
            <button
              onClick={onClose}
              disabled={isUploading}
              className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Dropzone area */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 mb-4 text-center transition-colors ${
              isDragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 bg-gray-50'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="mb-2 text-sm text-gray-600">
              {isDragActive
                ? 'Drop files here...'
                : 'Drag and drop files or folders here'}
            </p>
            <p className="mb-4 text-xs text-gray-500">or</p>

            {/* File and Folder selection buttons */}
            <div className="flex justify-center gap-4">
              <label className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md cursor-pointer hover:bg-primary-700">
                Choose Files
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={isUploading}
                />
              </label>

              <label className="px-4 py-2 text-sm font-medium text-primary-600 bg-white border border-primary-600 rounded-md cursor-pointer hover:bg-primary-50">
                Choose Folder
                <input
                  type="file"
                  webkitdirectory=""
                  directory=""
                  multiple
                  className="hidden"
                  onChange={handleFolderSelect}
                  disabled={isUploading}
                />
              </label>
            </div>
          </div>

          {/* Upload queue and empty folders */}
          {(uploadQueue.length > 0 || emptyFolders.length > 0) && (
            <div className="mb-4">
              {/* Clear All button for folders and files */}
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">
                  {uploadQueue.length > 0 && emptyFolders.length > 0 &&
                    `${uploadQueue.length} file(s) and ${emptyFolders.length} folder(s)`}
                  {uploadQueue.length > 0 && emptyFolders.length === 0 &&
                    `${uploadQueue.length} file(s)`}
                  {uploadQueue.length === 0 && emptyFolders.length > 0 &&
                    `${emptyFolders.length} folder(s)`}
                </h4>
                <button
                  onClick={clearAll}
                  disabled={isUploading}
                  className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  Clear All
                </button>
              </div>

              {/* Folder Structure Preview */}
              {(() => {
                const folders = extractFolderStructure();
                if (folders.length > 0) {
                  return (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <h5 className="text-xs font-semibold text-blue-900 mb-2 flex items-center">
                        <Folder className="w-4 h-4 mr-1" />
                        Folders to be created ({folders.length})
                      </h5>
                      <div className="text-xs text-blue-700 space-y-1 max-h-20 overflow-y-auto">
                        {folders.map((folder, idx) => {
                          const depth = folder.split('/').length - 1;
                          return (
                            <div key={idx} className="flex items-center" style={{ paddingLeft: `${depth * 12}px` }}>
                              <ChevronRight className="w-3 h-3 mr-1 flex-shrink-0" />
                              <span className="truncate">{folder.split('/').pop()}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
              })()}

              {uploadQueue.length > 0 && (
                <>
                  <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                <AnimatePresence>
                  {uploadQueue.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      className="flex items-center gap-3 p-3 border-b border-gray-100 last:border-b-0"
                    >
                      {/* File icon */}
                      <div className="flex-shrink-0">
                        <File className="w-5 h-5 text-gray-400" />
                      </div>

                      {/* File info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {item.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.path.includes('/') ? (
                            <span className="mr-2 text-blue-600">
                              <Folder className="w-3 h-3 inline mr-1" />
                              {item.path.split('/').slice(0, -1).join('/')}
                            </span>
                          ) : (
                            <span className="mr-2 text-gray-400">Root folder</span>
                          )}
                          <span>{formatBytes(item.size)}</span>
                        </p>

                        {/* Progress bar */}
                        {item.status === 'uploading' && (
                          <div className="w-full h-1 mt-1 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary-600 transition-all duration-300"
                              style={{ width: `${item.progress}%` }}
                            />
                          </div>
                        )}

                        {/* Error message */}
                        {item.status === 'error' && (
                          <p className="text-xs text-red-600 mt-1">{item.error}</p>
                        )}
                      </div>

                      {/* Status icon */}
                      <div className="flex-shrink-0">
                        {!isUploading && (
                          <button
                            onClick={() => removeFile(index)}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
                </>
              )}
            </div>
          )}

          {/* Footer actions */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={isUploading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {isUploading ? 'Uploading...' : 'Cancel'}
            </button>
            <button
              onClick={handleUploadAll}
              disabled={(uploadQueue.length === 0 && emptyFolders.length === 0) || isUploading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUploading && <Upload className="w-4 h-4 animate-pulse" />}
              {isUploading ? 'Uploading...' : (() => {
                const folderCount = extractFolderStructure().length;
                const fileCount = uploadQueue.length;
                const parts = [];
                if (fileCount > 0) parts.push(`${fileCount} file(s)`);
                if (folderCount > 0) parts.push(`${folderCount} folder(s)`);
                return parts.length > 0 ? `Upload ${parts.join(' & ')}` : 'Upload';
              })()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
